// 在全局变量部分添加节流相关变量
import { PersonnelApi, TagApi } from '@wiz-api/personnel-position'
import {
  get,
  isEmpty,
  map,
  filter,
  find,
  remove,
  forEach,
  cloneDeep,
  some,
  uniq,
  concat,
} from 'lodash'
import { Message } from 'element-ui'

const isFuXian = Wiz.globalSettings.globalConfig.FuXian
const personnelApi = new PersonnelApi()
const tagApi = new TagApi()
// 读取websocket连接地址
const socketPath =
  get(Wiz, 'globalSettings.dynamicModules.modules["visualization-cockpit"].config')
    ?.PersonnelPositionPath || ''
// 全部人员列表（不管在不在线）
let personnelList = []
// 当前在线的列表
let onlineTagList = []
// 当前需要更新的实时定位数据
let currentPositionList = []
// 当前已经加载并且显示的人员
let currentVisiblePerson = []
let socketObject = null
// 在当前楼层的tagId的数组
let filterTagList = []
// 是否在楼层中进行筛选
let isInFloor = false
// 是否展示
let isDisplay = false
// 所有创建过的人员
let allDataList = []
// 销毁的标识
let isDestroy = false
let cloudRender
let sceneId = ''
let socketDataHandler = null

// 节流相关变量
let lastProcessTime = 0 // 上次处理数据的时间戳
let throttleTimer = null // 节流定时器
let cachedPositionData = {} // 缓存位置数据的字典，按tagId存储最新值
let cachedOnlineData = {} // 缓存在线状态数据的字典，按tagId存储最新值
let isFirstProcess = true // 是否是首次处理

// 初始化数据
const initData = () => {
  personnelList = []
  onlineTagList = []
  currentPositionList = []
  currentVisiblePerson = []
  filterTagList = []
  isInFloor = false
  isDisplay = false
  allDataList = []
  sceneId = ''
  socketObject = null
  isDestroy = false
  socketDataHandler = null

  // 重置节流相关变量
  lastProcessTime = 0
  if (throttleTimer) {
    clearTimeout(throttleTimer)
    throttleTimer = null
  }
  cachedPositionData = {}
  cachedOnlineData = {}
  isFirstProcess = true
}

// 打开人员详情
const openPersonnelDetail = async (personnelId, tagCode, isFollow, isTrajectory = false) => {
  if (!personnelId || !tagCode) {
    return
  }
  if (!isTrajectory) {
    if (!find(allDataList, (item) => { return item === tagCode })) {
      return Message.warning({
        showClose: true,
        message: '人员暂未创建完毕，请稍后重试',
      })
    }
    if (!find(currentVisiblePerson, (item) => { return item === tagCode })) {
      return Message.warning({
        showClose: true,
        message: '该人员已下线，请刷新数据',
      })
    }
  }
  const openEvent = new CustomEvent('openComponent', {
    detail: {
      toCom: {
        code: isFuXian ? 'FuxianPersonnelInfo' : 'PersonnelInfo',
        origin: 'tag',
        name: '人员信息',
        originTagName: 'PersonnelList',
      },
      type: 'tags',
      props: {
        personnelId,
        tagCode,
        isTrajectory,
        isFollow
      },
    },
  })
  window.dispatchEvent(openEvent)
}

// rgba转换为十六进制
const translateColor = (rgbaString) => {
  // 利用正则表达式匹配出RGBA中的R、G、B和A通道的值
  const values = rgbaString.match(/\d+(\.\d+)?/g)
  const r = parseInt(values[0])
  const g = parseInt(values[1])
  const b = parseInt(values[2])
  const a = parseFloat(values[3])

  // 将R、G、B三个颜色通道的值转换为十六进制，并补齐到两位
  const rHex = r.toString(16).padStart(2, '0')
  const gHex = g.toString(16).padStart(2, '0')
  const bHex = b.toString(16).padStart(2, '0')

  // 将A通道的值转换为十六进制，并补齐到两位
  const aHex = Math.round(a * 255)
    .toString(16)
    .padStart(2, '0')

  // 拼接R、G、B、A四个通道的十六进制数值
  const hexColor = `#${rHex}${gHex}${bHex}${aHex}`

  return hexColor
}

// 获取人员列表
const getPersonnelList = async () => {
  const [err, res] = await tagApi.getTagList({
    keyword: '',
    type: '',
    departmentId: '',
    maxResultCount: 1000,
    skipCount: 0,
  })
  if (!err && !isEmpty(res)) {
    personnelList = res.items.filter((item) => {
      return item.type === 2
    })
  }
}

// 处理socket数据的函数
const processSocketData = async (res) => {
  if (isDestroy) return

  // 两种情况：res.positionList和res.onLineList只有一个有值
  if (!isEmpty(res) && isEmpty(res.positionList) && !isEmpty(res.onLineList)) {
    // 处理在线状态数据
    // 筛选出设置了标签的人员
    onlineTagList = filter(res.onLineList, (el) => {
      const personDetail = find(personnelList, ['tagCode', el.tagId])
      return personDetail && personDetail.personnelName && personDetail.personnelName.trim() !== ''
    })

    // 添加名字字段
    onlineTagList = forEach(onlineTagList, (item) => {
      const personDetail = find(personnelList, ['tagCode', item.tagId])
      if (!isEmpty(personDetail)) {
        item.name = personDetail.personnelName
      }
    })

    // 与之前创建完成人员相比，筛选出消失的人员
    const hiddenList = remove(currentVisiblePerson, (el) => {
      return !find(onlineTagList, ['tagId', el])
    })

    // 把消失的人员隐藏
    if (!isEmpty(hiddenList)) {
      cloudRender.PosViewer.setPosViewerVisible(hiddenList, false)
    }

    // 需要创建的人员列表（新进来的人员的isNew为true）
    const createArr = map(
      filter(onlineTagList, (el) => {
        // isNew并且没有创建过
        return (
          el.isNew &&
          !some(allDataList, (it) => {
            return it === el.tagId
          })
        )
      }),
      (item) => {
        const currentTag = find(personnelList, (tag) => { return tag.tagCode === item.tagId })
        const background = currentTag?.color ? translateColor(currentTag.color) : '#2196f3'
        return {
          id: item.tagId,
          position: [item.x, item.y, item.z],
          markerOption: {
            background,
            text: item.name,
            markPosType: 4,
            nodeOffset: [0, -5],
          },
        }
      }
    )

    // 重新上线的人员
    const reshowList = map(
      filter(onlineTagList, (el) => {
        // 临时下线了
        return (
          el.isNew &&
          !some(currentVisiblePerson, (it) => {
            return it === el.tagId
          }) &&
          some(allDataList, (i) => {
            return i === el.tagId
          })
        )
      }),
      (item) => {
        return item.tagId
      }
    )

    // 临时下线的人员再次上线
    await cloudRender.PosViewer.setPosViewerVisible(reshowList, true)
    currentVisiblePerson = uniq(concat(currentVisiblePerson, reshowList))

    // 批量创建人员
    const createdResult = await cloudRender.PosViewer.createPosViewer(createArr)
    if (createdResult.status && !isEmpty(createdResult.data)) {
      if (isDestroy) {
        await cloudRender.PosViewer.destroyPosviewer(createdResult.data)
      } else {
        currentVisiblePerson = uniq(concat(currentVisiblePerson, createdResult.data))
        // 把所有创建过的人存起来
        allDataList = uniq(concat(allDataList, currentVisiblePerson))
      }
    }

    // 传递给人员定位功能弹框当前在线的人员标签列表
    const onlineEvent = new CustomEvent('getOnlineTagList', {
      detail: {
        onlineTagList: currentVisiblePerson,
        allLineList: res.onLineList,
      },
    })
    window.dispatchEvent(onlineEvent)
  } else if (!isEmpty(res) && !isEmpty(res.positionList) && isEmpty(res.onLineList)) {
    // 处理位置数据
    // 当前需要显示的人员：筛选出已经创建并且绑定了标签的人员
    const filterPositionList = filter(res.positionList, (el) => {
      return (
        find(personnelList, ['tagCode', el.tagId]) &&
        find(currentVisiblePerson, (item) => {
          return item === el.tagId
        })
      )
    })

    // 更新全局在线人员列表
    const positionNowEvent = new CustomEvent('setOnlinePositionList', {
      detail: {
        position: filterPositionList,
      },
    })
    window.dispatchEvent(positionNowEvent)

    currentPositionList = filter(filterPositionList, (el) => {
      return (
        // 如果需要在楼层中筛选
        !isInFloor ||
        find(filterTagList, (item) => {
          return item === el.tagId
        })
      )
    })

    // 兜底：positionList 到来但对应标签未创建时，先根据 positionList 补建
    const havePositionList = filter(res.positionList, (el) => {
      const personDetail = find(personnelList, ['tagCode', el.tagId])
      return personDetail && personDetail.personnelName && personDetail.personnelName.trim() !== ''
    })

    const toCreateFromPosition = map(
      filter(havePositionList, (el) => {
        return !some(allDataList, (id) => { return id === el.tagId })
      }),
      (item) => {
        const currentTag = find(personnelList, (tag) => { return tag.tagCode === item.tagId })
        const background = currentTag?.color ? translateColor(currentTag.color) : '#2196f3'
        return {
          id: item.tagId,
          position: [item.x, item.y, item.z],
          markerOption: {
            background,
            text: currentTag?.personnelName || '',
            markPosType: 4,
            nodeOffset: [0, -5],
          },
        }
      }
    )

    // 考虑到太快的通信导致的可能残留标签，多重多次判断
    if (!isEmpty(toCreateFromPosition)) {
      if (!isDestroy) {
        const createdFromPos = await cloudRender.PosViewer.createPosViewer(toCreateFromPosition)
        if (createdFromPos.status && !isEmpty(createdFromPos.data)) {
          currentVisiblePerson = uniq(concat(currentVisiblePerson, createdFromPos.data))
          // 把所有创建过的人存起来
          allDataList = uniq(concat(allDataList, currentVisiblePerson))
        }
      }
    }

    // 批量设置位置
    await cloudRender.PosViewer.setPosition(
      map(currentPositionList, (item) => {
        return {
          id: item.tagId,
          position: [item.x, item.y, item.z],
        }
      })
    )
  } else if (!isEmpty(res) && isEmpty(res.positionList) && isEmpty(res.onLineList)) {
    // 如果都没值则全部下线，清空数值
    if (currentVisiblePerson.length > 0) {
      cloudRender.PosViewer.setPosViewerVisible(currentVisiblePerson, false)
      currentVisiblePerson = []
      // 传递给人员定位功能弹框当前在线的人员标签列表
      const onlineEvent = new CustomEvent('getOnlineTagList', {
        detail: {
          onlineTagList: [],
          allLineList: res.onLineList,
        },
      })
      window.dispatchEvent(onlineEvent)
    }
  }

  // 更新页面数据引用
  if (window.__POWERED_BY_QIANKUN__) {
    wiz.personData = {
      routerUrl: window.location.href,
    }
  } else {
    window.personData = {
      routerUrl: window.location.href,
    }
  }
}

// 连接websocket
const connetSocket = async () => {
  const socketUrl = `${socketPath}/Position?modelId=${sceneId}&siteId=3a0743fd-2f61-51c2-3bfd-415e973ef230`
  socketObject = new WebSocket(socketUrl)
  socketObject.onmessage = async ({ data }) => {
    if (data.indexOf('连接成功') === 0) {
      console.log('连接成功!')
    } else {
      const res = cloneDeep(JSON.parse(data))

      // 节流逻辑
      if (isFirstProcess) {
        // 首次直接处理
        isFirstProcess = false
        lastProcessTime = Date.now()
        socketDataHandler = processSocketData(res)
      } else {
        // 缓存数据
        if (!isEmpty(res) && !isEmpty(res.positionList)) {
          // 缓存位置数据，按tagId覆盖
          res.positionList.forEach((item) => {
            cachedPositionData[item.tagId] = item
          })
        }
        if (!isEmpty(res) && !isEmpty(res.onLineList)) {
          // 缓存在线状态数据，按tagId覆盖
          res.onLineList.forEach((item) => {
            cachedOnlineData[item.tagId] = item
          })
        }

        // 只有当定时器不存在时才创建新的定时器，确保每5秒处理一次
        if (!throttleTimer) {
          throttleTimer = setTimeout(async () => {
            // 合并缓存的数据
            const mergedRes = {
              positionList: Object.values(cachedPositionData),
              onLineList: Object.values(cachedOnlineData),
            }

            // 清空缓存
            cachedPositionData = {}
            cachedOnlineData = {}

            // 清除定时器标记
            throttleTimer = null

            // 处理合并后的数据
            socketDataHandler = processSocketData(mergedRes)
            await socketDataHandler
            lastProcessTime = Date.now()
          }, 2500)
        }
      }
    }
  }
}

// 标签事件
const markerClick = (e) => {
  // 左键点击并且有id
  if (e.type === 1 && !isEmpty(e.id)) {
    const currentPersonnel = find(personnelList, ['tagCode', e.id])
    if (!isEmpty(currentPersonnel)) {
      openPersonnelDetail(currentPersonnel.personnelId, e.id, false)
    }
  }
}

const filterPositionHandler = (e) => {
  filterTagList = cloneDeep(e.detail.tagIdList)
  isInFloor = true
  if (!isDisplay) {
    // 隐藏不在当前楼层的人员
    forEach(currentVisiblePerson, (item) => {
      // 如果在楼层中则显示，不在则隐藏
      if (
        !some(filterTagList, (i) => {
          return i === item
        })
      ) {
        cloudRender.PosViewer.setPosViewerVisible(item, false)
      } else {
        cloudRender.PosViewer.setPosViewerVisible(item, true)
      }
    })
  }
}

const operateSocketHandler = async (e) => {
  if (e.detail) {
    if (e.detail.type) {
      connetSocket()
      cloudRender.PosViewer.setPosViewerVisible(currentVisiblePerson, true)
    } else {
      // 断开websocket连接
      if (socketObject) {
        socketObject.close()
      }
      // 隐藏所有人员
      cloudRender.PosViewer.setPosViewerVisible(currentVisiblePerson, false)
    }
  }
}

// 创建人员
const create = async (player, propSceneId, propCloudRender) => {
  if (!player.Native) {
    return
  }
  // 初始化数据
  initData()
  cloudRender = propCloudRender
  sceneId = propSceneId
  await getPersonnelList()
  connetSocket()
  cloudRender.Dispatch.on('PosViewerMarker', markerClick)
  window.addEventListener('filterPositionList', filterPositionHandler)
  window.addEventListener('operateSocket', operateSocketHandler)
}

// 销毁人员
const destroy = async (player) => {
  console.log('销毁人员', player)
  // 断开websocket连接
  if (socketObject) {
    socketObject.close()
  }

  // 清除节流定时器
  if (throttleTimer) {
    clearTimeout(throttleTimer)
    throttleTimer = null
  }

  // 传递给人员定位功能弹框当前在线的人员标签列表
  const onlineEvent = new CustomEvent('getOnlineTagList', {
    detail: {
      onlineTagList: [],
      allLineList: [],
    },
  })
  window.dispatchEvent(onlineEvent)

  isDestroy = true
  if (!player.Native || !cloudRender.PosViewer) {
    return
  }

  // 等待正在处理的socket数据完成
  if (socketDataHandler) {
    await socketDataHandler
  }

  await cloudRender.PosViewer.destroyPosviewer(allDataList)
  // 取消标签的事件监听
  cloudRender.Dispatch.off('PosViewerMarker', markerClick)
  window.removeEventListener('filterPositionList', filterPositionHandler)
  window.removeEventListener('operateSocket', operateSocketHandler)
}

const hidden = async () => {
  isInFloor = true
  isDisplay = false
}

const display = async () => {
  isInFloor = false
  isDisplay = true
  // 把全部人员显示出来
  await cloudRender.PosViewer.setPosViewerVisible(currentVisiblePerson, true)
}

const onlinePerson = () => {
  // 返回当前在线的人员
  return onlineTagList
}

export default {
  create,
  destroy,
  hidden,
  display,
  openPersonnelDetail,
  onlinePerson,
}
